import React, { useEffect, useState } from 'react';
import userProfile from '../../../../assets/images/userProfile.svg';
import {
  Button,
  Checkbox,
  Drawer,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Pagination } from '../../../../components';
import { useParams } from 'react-router-dom';
import ControllerNameEdit from './controllerNameEdit';
import { UrlToBase } from '../../../../components/urlToBase64';
import { useHook as RoleHook } from '../../Iam/rolemanagement/useHook';
import { useHook as ControllerHook } from './useHook';
import { useSelector } from 'react-redux';
import ControllerParamForm from './controllerParameterForm';
import { Unauthorized } from '../../../../components/index';
import { ErrorMessage } from '../../../../permissionErrorMessage/PermissionMessage';

interface Role {
  roleID: string | number;
  roleName?: string;
}
interface Props {
  pagePermissionIDs: unknown;
}
const EditController: React.FC<Props> = ({ pagePermissionIDs }) => {
  const canGetControllers =
    Array.isArray(pagePermissionIDs) &&
    pagePermissionIDs.includes('controllers::get');
  const canRolelistPermission =
    Array.isArray(pagePermissionIDs) &&
    pagePermissionIDs.includes('roles::list');

  const requiredAccess = (
    <section className="required_access_section">
      <p>you require below access:</p>
      <ul>
        <li>{ErrorMessage['controllers::get'] || ''}</li>
        <li>{ErrorMessage['roles::list'] || ''}</li>
      </ul>
    </section>
  );
  const { id } = useParams();
  const { fetchGetAllRoleList } = RoleHook();
  const { fetchGetControllerDetails, editController } = ControllerHook();
  const [openEdit, setOpenEdit] = useState(false);
  const [controllerDetails, setControllerDetails] = useState(Object);
  const [open, setOpen] = React.useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    variableName: '',
    behaviour: '',
    customerAccess: false,
    comment: '',
  });
  const [paramsPayload, setParamsPayload]: any = useState([]);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [imageData, setImageData] = useState<string | null>(null);
  const { allRole } = useSelector((state: any) => state.role);

  const handleRowsPerPageChange = (value: number) => {
    setRowsPerPage(value);
    setCurrentPage(1);
    const startIndex = 0;
    const endIndex = startIndex + value;
    const pageData = controllerDetails?.parameters?.slice(startIndex, endIndex);
    setParamsPayload(pageData);
    const totalPage = Math.ceil(controllerDetails?.parameters?.length / value);
    setTotalPages(totalPage ? totalPage : 1);
  };
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const pageData = controllerDetails?.parameters?.slice(startIndex, endIndex);
    setParamsPayload(pageData);
  };

  useEffect(() => {
    if (controllerDetails) {
      const imageUrl = controllerDetails?.image;
      if (imageUrl) {
        UrlToBase(imageUrl)
          .then((base64String) => {
            setImageData(base64String);
          })
          .catch((error) => console.error(error));
      }
    }
  }, [controllerDetails]);

  const getControllerDetails = async () => {
    await fetchGetControllerDetails({ controllerID: id })
      .then((response: any) => {
        if (response?.data.data) {
          setControllerDetails(response?.data.data);

          const totalPage = Math.ceil(
            response?.data?.data?.parameters?.length / rowsPerPage,
          );
          setTotalPages(totalPage ? totalPage : 1);
          const startIndex = (currentPage - 1) * rowsPerPage;
          const endIndex = startIndex + rowsPerPage;
          const pageData = response?.data?.data?.parameters?.slice(
            startIndex,
            endIndex,
          );

          setParamsPayload(pageData);
        }
      })
      .catch(() => {
        setControllerDetails({});
      });
  };

  const findrolename = (roleid: string) => {
    if (allRole?.length > 0) {
      if (roleid) {
        const findrole = allRole?.find((param: Role) => param.roleID == roleid);
        return findrole?.roleName ? findrole?.roleName : roleid;
      } else {
        return roleid;
      }
    } else {
      return roleid;
    }
  };

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
    setFormData({
      name: '',
      type: '',
      variableName: '',
      behaviour: '',
      customerAccess: false,
      comment: '',
    });
    if (newOpen === false) {
      setEditIndex(null);
    }
  };

  const submitParam = async (data: any) => {
    if (!(editIndex === null)) {
      const updatedItems = [...paramsPayload];
      updatedItems[editIndex] = {
        name: data?.name,
        type: data?.type,
        variableName: data?.variableName?.replace(/\s/g, ''),
        behaviour: data?.behaviour ? data?.behaviour : 'READ',
        accessPermissions: data?.accessPermissions,
        customerAccess: data?.customerAccess,
        comment: data?.comment,
      };
      const imageType = imageData?.substring(11, imageData?.indexOf(';'));
      const imageWithoutPrefix = imageData?.substring(
        imageData?.indexOf(',') + 1,
      );

      await editController({
        controllerID: id,
        controllerName: controllerDetails?.controllerName,
        model: controllerDetails?.model,
        image: imageWithoutPrefix ? imageWithoutPrefix : '',
        ext: imageType ? imageType : '',
        parameters: updatedItems,
      });
      getControllerDetails();
      setEditIndex(null);
    }
    setOpen(false);
  };

  const handleParamEdit = (editIndex: number) => {
    setEditIndex(editIndex);
    const EditData = paramsPayload[editIndex];
    setFormData(EditData);
    setOpen(true);
  };

  useEffect(() => {
    if (canGetControllers && canRolelistPermission) {
      getControllerDetails();
      if (!(allRole?.length > 0)) {
        fetchGetAllRoleList({});
      }
    }
  }, [canGetControllers, canRolelistPermission]);
  const handleCloseEdit = () => {
    setOpenEdit(false);
    getControllerDetails();
  };

  return (
    <>
      {!canGetControllers || !canRolelistPermission ? (
        <Unauthorized requiredAccess={requiredAccess} />
      ) : (
        <>
          {openEdit ? (
            <>
              <ControllerNameEdit
                editData={controllerDetails}
                handleCloseEdit={handleCloseEdit}
              />
            </>
          ) : (
            <>
              <div className="edit_controller-header">
                <div className="edit_controller-header_content">
                  <img
                    src={
                      controllerDetails?.image
                        ? controllerDetails?.image
                        : userProfile
                    }
                    width="80"
                    height="80"
                    alt="controller"
                  />
                  <div>
                    <h4>{controllerDetails?.controllerName}</h4>
                    <p>{controllerDetails?.model}</p>
                  </div>
                </div>

                <div className="edit_controller-header_icons">
                  <Button onClick={() => setOpenEdit(true)}>
                    <i className="edit-icon bi bi-pencil-square bi-lg"></i>
                  </Button>
                </div>
              </div>
              <div className="edit_controller-parameters">
                <h4>Parameters</h4>
                <TableContainer
                  className="edit_controller-parameters-table controller_table"
                  component={Paper}
                >
                  <Table
                    className={
                      paramsPayload?.length > 0
                        ? 'fullwidthcontroller_table'
                        : 'nodatacontroller_table'
                    }
                  >
                    <TableHead className="controller_tableheader">
                      <TableRow>
                        <TableCell>Names</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Variable Name</TableCell>
                        <TableCell>Behaviour</TableCell>
                        <TableCell>Access Permission</TableCell>
                        <TableCell>Customer Access</TableCell>
                        <TableCell>Comment</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paramsPayload?.length > 0 ? (
                        paramsPayload?.map((controller: any, index: number) => (
                          <TableRow key={index}>
                            <TableCell className="controller_management_name">
                              <div className="controller_management_name-data">
                                <p>{controller?.name}</p>
                              </div>
                            </TableCell>
                            <TableCell className="controller_management_type">
                              <p className="controller_management_model-data">
                                {controller?.type}
                              </p>
                            </TableCell>
                            <TableCell className="controller_management_variable">
                              <p className="controller_management_model-data">
                                {controller?.variableName}
                              </p>
                            </TableCell>
                            <TableCell className="controller_management_behaviour">
                              <p className="controller_management_model-data">
                                {controller?.behaviour
                                  ? controller?.behaviour
                                  : 'READ'}
                              </p>
                            </TableCell>
                            <TableCell className="controller_management_permission">
                              <div>
                                {controller?.accessPermissions?.length > 0 &&
                                  controller?.accessPermissions?.map(
                                    (Permission: string, index: number) => (
                                      <div
                                        key={index}
                                        className="controller_permission-data"
                                      >
                                        <Typography variant="body1">
                                          {findrolename(Permission)}
                                        </Typography>
                                      </div>
                                    ),
                                  )}
                              </div>
                            </TableCell>
                            <TableCell className="controller_management_access">
                              <Checkbox
                                checked={controller.customerAccess}
                                disabled={true}
                              />
                            </TableCell>
                            <TableCell className="controller_management_comment">
                              {controller?.comment?.length > 30 ? (
                                <Tooltip title={controller?.comment} arrow>
                                  <p className="controller_management_model-data">
                                    {controller?.comment?.slice(0, 30)}....
                                  </p>
                                </Tooltip>
                              ) : (
                                <p className="controller_management_model-data">
                                  {controller?.comment?.slice(0, 30) || ''}
                                </p>
                              )}
                            </TableCell>
                            <TableCell className="controller_management_icons-data">
                              <div className="form-action-btn">
                                <Button
                                  className="btn-transparent border-0 img-background-form"
                                  onClick={() => handleParamEdit(index)}
                                >
                                  <i className="edit-icon bi bi-pencil-square bi-lg"></i>
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <></>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Pagination
                  rowsPerPageOptions={[5, 10, 20, 30]}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  totalPages={totalPages}
                />
              </div>
            </>
          )}

          <Drawer open={open} anchor="right" onClose={toggleDrawer(false)}>
            <ControllerParamForm
              paramCloseDrawer={toggleDrawer(false)}
              paramSubmit={submitParam}
              formEditData={formData}
              editPage={true}
            />
          </Drawer>
        </>
      )}
    </>
  );
};

export default EditController;
